import { EMPTY_SCAFFOLD_SELECTION } from "../interface";
import { IScaffoldActionCellClicked, ScaffoldState } from "./interface";
import {
  addCellRangeToSelection,
  hasAnySelection,
  setCellRangeSelection,
  toggleCellInSelection,
} from "./scaffoldSelection";

/**
 * Handles the CELL_CLICKED action by updating the selection state based on the
 * modifier keys held during the click.
 *
 * - Shift+click with a cell anchor: range-selects from the anchor to the clicked cell.
 * - Ctrl/Meta+click: toggles the clicked cell in the selection and sets the anchor.
 * - Plain click with an active selection: clears the selection.
 * - Plain click with no selection: no-op (returns same state).
 *
 * @param action - The CELL_CLICKED action carrying the cell coordinates and modifier keys.
 * @param state - The current scaffold state.
 * @returns Updated scaffold state with the new selection and anchor.
 */
export const scr_cellClicked = <T>(
  action: IScaffoldActionCellClicked,
  state: ScaffoldState<T>,
): ScaffoldState<T> => {
  const { rowId, colId, ctrlKey, metaKey, shiftKey } = action;
  const { selection, selectionAnchor, prevRows, prevCols } = state;

  if (shiftKey) {
    if (selectionAnchor && selectionAnchor.type === "cell") {
      return {
        ...state,
        selection:
          ctrlKey || metaKey
            ? addCellRangeToSelection(
                selection,
                prevRows,
                prevCols,
                selectionAnchor.rowId,
                selectionAnchor.colId,
                rowId,
                colId,
              )
            : setCellRangeSelection(
                prevRows,
                prevCols,
                selectionAnchor.rowId,
                selectionAnchor.colId,
                rowId,
                colId,
              ),
      };
    }
    return {
      ...state,
      selection: { ...EMPTY_SCAFFOLD_SELECTION, selectedCells: [{ rowId, colId: [colId] }] },
      selectionAnchor: { type: "cell", rowId, colId },
    };
  }
  if (ctrlKey || metaKey) {
    return {
      ...state,
      selection: toggleCellInSelection(selection, prevRows, prevCols, rowId, colId),
      selectionAnchor: { type: "cell", rowId, colId },
    };
  }
  if (hasAnySelection(selection)) {
    return { ...state, selection: EMPTY_SCAFFOLD_SELECTION, selectionAnchor: null };
  }
  return state;
};
